// The checkout's hold timer and keep-alive: WHEN to show the time left, WHEN
// to ask the server for more time, and WHAT to say. Pure: no React, no
// network, no Date.now(). Every function takes the time it should judge by, so
// the spec (e2e/hold-timer-logic.spec.ts) drives it with a fake clock and the
// hook (use-hold-keepalive.ts) with the real one.
//
// The owner's rules (brief 2026-09-25 "pay now, details later" section 7.4,
// applied to today's drawer checkout):
//   * show the time left only when 5 minutes or less remain;
//   * ask for more time (extend_cart_hold, 20260927090010) when 4 minutes or
//     less remain AND the guest typed or tapped in the checkout in the last
//     2 minutes; one call in flight; 'busy' tries again on the next tick;
//   * at the cap, say "We can hold this for N more minutes";
//   * cart_mismatch: read the page's rows again and retry once, 5 s later
//     (never straight away with the same ids: that is a certain second
//     mismatch). The wait is what lets a Remove still on its way to the
//     server (the cart drops the line before cancel_cart_booking answers)
//     land first. A second mismatch stops the keep-alive for these ids and
//     the timer says "We can hold this for N more minutes", which is then
//     the truth. A new set of ids (the guest changed the cart) gets its own
//     try. Never cancel anything: Remove stays the guest's action;
//   * hold_expired naming one of the page's holds: say "Your hold ended —
//     please check availability again" and stop. One naming only a stray (a
//     row the page does not show) is not the guest's hold ending;
//   * every one of the page's rows gone (cancelled by staff, released in
//     another tab, confirmed elsewhere) or no longer this cart's: the same
//     message, never a timer that quietly disappears;
//   * a cart with a spa item, and a staff pay-link cart (not_extendable),
//     never extend: the timer only.
// Once ended, CheckoutView disables Pay and the banner offers "Back to your
// visit" (HoldTimer's onEndedChange).
//
// The expiry always comes from the server (get_cart_hold_state, then each
// extend's answer), never from an assumed 15 minutes: the window is the
// venue's "Deposit payment window" (PR #1433), and a hold can already be
// longer than extend_cart_hold's cap. The countdown runs on the SERVER's
// clock: the read returns the server's now, and the difference from the
// device's clock (skewMs) is applied to every comparison.
import type {
  CartHoldStateResult,
  ExtendCartHoldResult,
} from "@vertu/shared";

export const SHOW_AT_MS = 5 * 60_000;
export const EXTEND_AT_MS = 4 * 60_000;
export const ACTIVE_WITHIN_MS = 2 * 60_000;
/** The page re-judges the hold once a second (use-hold-keepalive.ts). */
export const TICK_MS = 1_000;
/** 'busy' (another transaction holds the rows for milliseconds): the next tick. */
export const BUSY_RETRY_MS = TICK_MS;
/** A call that failed in transport (maybe offline): 5 s, not every tick. */
export const RETRY_GAP_MS = 5_000;
/** A stray row's lapse refuses every extend until the expiry sweep flips it. */
export const STRAY_GAP_MS = 15_000;
/** A failed read, and the re-read that confirms a lapse at 0:00. */
export const READ_RETRY_MS = 10_000;
/** Failed reads back off (10 s, 20 s, 40 s, then every 60 s): a page served
 *  before get_cart_hold_state is deployed must not poll every 10 s. */
export const READ_BACKOFF_MAX_MS = 60_000;
/** cart_mismatch: the one retry waits this long (after a read of the rows). */
export const MISMATCH_RETRY_MS = 5_000;
/** The device's clock moved this much more (or less) than the page's own
 *  monotonic clock between two ticks: re-read the server's clock. */
export const CLOCK_JUMP_MS = 2_000;

export const LAPSE_MESSAGE = "Your hold ended — please check availability again";

export type KeepAliveState = {
  /** A read has answered: expiresAt null now means "no clock", not "not yet known". */
  known: boolean;
  /** When the page's holds lapse, in SERVER ms. Null: no clock (or not known yet). */
  expiresAt: number | null;
  /** The latest extend_cart_hold can ever push the hold to (server ms), from the last ok. */
  capAt: number | null;
  /** Server clock − device clock, from the last read. */
  skewMs: number;
  /** False for a cart with a spa item, and once the server refuses the cart outright. */
  extendable: boolean;
  /** The one cart_mismatch retry was used for this id signature. */
  mismatchRetriedFor: string | null;
  /** Keep-alive given up for this id signature (a second cart_mismatch). */
  stoppedFor: string | null;
  /** Reads in a row that failed in transport (the back-off). */
  readFailures: number;
  /** Device ms of the last key press, tap or edit in the checkout. */
  lastActivityAt: number | null;
  inFlight: "extend" | "read" | null;
  /** No extend before this device ms (busy, a failed call, a stray's lapse). */
  extendNotBefore: number;
  /** No read before this device ms. */
  readNotBefore: number;
  /** The server confirmed that one of the page's holds lapsed. Final. */
  ended: boolean;
};

export function initialKeepAlive(input: { hasSpaItem: boolean }): KeepAliveState {
  return {
    known: false,
    expiresAt: null,
    capAt: null,
    skewMs: 0,
    extendable: !input.hasSpaItem,
    mismatchRetriedFor: null,
    stoppedFor: null,
    readFailures: 0,
    lastActivityAt: null,
    inFlight: null,
    extendNotBefore: 0,
    readNotBefore: 0,
    ended: false,
  };
}

/**
 * The booking ids the page holds for this cart, sorted and de-duplicated: every
 * non-spa line's booking_id (a combo line sends each component's own line, and
 * a managed deposit may be sent). A spa line has no server row before payment.
 */
export function cartHoldIds(
  items: readonly { metadata?: Record<string, unknown> | null }[],
): string[] {
  const ids = new Set<string>();
  for (const it of items) {
    const md = it.metadata ?? {};
    if (md.source === "chidesk") continue;
    if (typeof md.booking_id === "string" && md.booking_id) ids.add(md.booking_id);
  }
  return [...ids].sort();
}

export function hasSpaItem(
  items: readonly { metadata?: Record<string, unknown> | null }[],
): boolean {
  return items.some((it) => it.metadata?.source === "chidesk");
}

/** Time left on the server's clock, in ms; null when there is no clock. */
export function remainingMs(s: KeepAliveState, now: number): number | null {
  if (s.expiresAt == null) return null;
  return s.expiresAt - (now + s.skewMs);
}

/** The server can push the hold no further (or it already runs past the cap). */
export function atCap(s: KeepAliveState): boolean {
  return s.capAt != null && s.expiresAt != null && s.expiresAt >= s.capAt;
}

/**
 * The page will ask for no more time: at the cap, or the keep-alive stopped
 * after a second cart_mismatch (the hook starts a fresh state for a new set
 * of ids, so a stop always belongs to the ids on screen). Either way "We can
 * hold this for N more minutes" is what is true.
 */
export function noMoreTime(s: KeepAliveState): boolean {
  return atCap(s) || s.stoppedFor != null;
}

/**
 * Between two ticks the device's clock moved `wallDelta` while the page's
 * monotonic clock moved `monoDelta`. A difference of CLOCK_JUMP_MS or more is
 * the device's clock being set (or the device waking from sleep): the skew
 * from the last read no longer holds.
 */
export function clockJumped(wallDelta: number, monoDelta: number): boolean {
  return Math.abs(wallDelta - monoDelta) >= CLOCK_JUMP_MS;
}

/** Read the server's clock again (after a clock jump). Nothing else changes. */
export function rereadClock(s: KeepAliveState): KeepAliveState {
  if (s.ended || s.expiresAt == null) return s;
  return { ...s, known: false, readNotBefore: 0 };
}

export function markActivity(s: KeepAliveState, now: number): KeepAliveState {
  return { ...s, lastActivityAt: now };
}

/** Would an extend call be allowed right now (every rule but "one in flight")? */
export function extendDue(s: KeepAliveState, now: number, signature: string): boolean {
  if (s.ended || !s.known || !s.extendable || s.stoppedFor === signature) return false;
  const left = remainingMs(s, now);
  if (left == null || left <= 0 || left > EXTEND_AT_MS) return false;
  if (s.lastActivityAt == null || now - s.lastActivityAt > ACTIVE_WITHIN_MS) return false;
  if (now < s.extendNotBefore) return false;
  return !atCap(s);
}

/**
 * What the page should do now: read the holds (first, after a failed read, or
 * to confirm a lapse at 0:00), ask for more time, or nothing.
 */
export function nextAction(
  s: KeepAliveState,
  now: number,
  signature: string,
): "read" | "extend" | null {
  if (s.ended || s.inFlight || signature === "") return null;
  if (!s.known) return now >= s.readNotBefore ? "read" : null;
  const left = remainingMs(s, now);
  if (left == null) return null;
  if (left <= 0) return now >= s.readNotBefore ? "read" : null;
  return extendDue(s, now, signature) ? "extend" : null;
}

export function startCall(s: KeepAliveState, kind: "read" | "extend"): KeepAliveState {
  return { ...s, inFlight: kind };
}

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/**
 * A get_cart_hold_state answer. `sentAt`/`receivedAt` are device ms around the
 * call: the server's `now` is taken as the midpoint, which sets skewMs.
 */
export function applyRead(
  s: KeepAliveState,
  result: CartHoldStateResult,
  at: { sentAt: number; receivedAt: number },
  pageIds: readonly string[],
): KeepAliveState {
  const next: KeepAliveState = {
    ...s,
    inFlight: null,
    readFailures: 0,
    readNotBefore: at.receivedAt + READ_RETRY_MS,
  };
  if (!result.ok) {
    // A transport failure (or a page served before the function exists):
    // try again later, still not knowing, backing off to once a minute. A
    // refusal of the token or the ids: this page will never learn a clock.
    if (result.reason === "unknown") {
      const gap = Math.min(READ_RETRY_MS * 2 ** s.readFailures, READ_BACKOFF_MAX_MS);
      return { ...next, readFailures: s.readFailures + 1, readNotBefore: at.receivedAt + gap };
    }
    return { ...next, known: true, expiresAt: null, extendable: false };
  }
  const serverNow = ms(result.now);
  const skewMs =
    serverNow == null ? s.skewMs : Math.round(serverNow - (at.sentAt + at.receivedAt) / 2);
  const page = new Set(pageIds);
  const holds = result.holds ?? [];
  const lapsed = holds.some((h) => h.state === "lapsed" && page.has(h.booking_id));
  // Every one of the page's rows gone (confirmed, cancelled, superseded) or
  // absent (no longer this cart's): nothing the page shows is held, and Pay
  // would charge none of it. Said, not a timer that silently disappears.
  const stillOurs = new Set(holds.filter((h) => h.state !== "gone").map((h) => h.booking_id));
  const allGone = pageIds.length > 0 && pageIds.every((id) => !stillOurs.has(id));
  return {
    ...next,
    known: true,
    skewMs,
    expiresAt: ms(result.hold_expires_at),
    ended: s.ended || lapsed || allGone,
  };
}

/** An extend_cart_hold answer. */
export function applyExtend(
  s: KeepAliveState,
  result: ExtendCartHoldResult,
  at: { receivedAt: number },
  pageIds: readonly string[],
  signature: string,
): { state: KeepAliveState } {
  const next: KeepAliveState = { ...s, inFlight: null };
  if (result.ok) {
    return {
      state: { ...next, expiresAt: ms(result.hold_expires_at), capAt: ms(result.cap_at) },
    };
  }
  switch (result.reason) {
    case "busy":
      // Never a lapse: keep the current expiry and try on the next tick.
      return { state: { ...next, extendNotBefore: at.receivedAt + BUSY_RETRY_MS } };
    case "unknown":
      // Transport: unknown whether it extended, never a lapse. Try again, 5 s on.
      return { state: { ...next, extendNotBefore: at.receivedAt + RETRY_GAP_MS } };
    case "not_extendable":
    case "invalid_cart_token":
      return { state: { ...next, extendable: false } };
    case "nothing_held":
      // No live hold on the server: stop asking, and read what the page's
      // rows are now (gone rows take the timer away; lapsed ones end it).
      return { state: { ...next, extendable: false, known: false } };
    case "hold_expired": {
      const page = new Set(pageIds);
      if (result.booking_ids.some((id) => page.has(id))) {
        return { state: { ...next, ended: true } };
      }
      // Only a stray the page does not show has lapsed: not this guest's
      // hold. The server refuses every extend until the sweep flips it.
      return { state: { ...next, extendNotBefore: at.receivedAt + STRAY_GAP_MS } };
    }
    case "cart_mismatch":
      // The server holds a different set of rows than the page shows. The
      // first time: read the page's rows again now (a row gone elsewhere
      // ends it, a lapsed one too) and retry once, MISMATCH_RETRY_MS on —
      // never straight away with the same ids, which only mismatches again.
      // The wait lets a Remove that is still reaching the server land. The
      // second time: stop asking for these ids; the timer says how much time
      // is left and that no more is coming. Nothing is ever cancelled here.
      if (s.mismatchRetriedFor !== signature) {
        return {
          state: {
            ...next,
            mismatchRetriedFor: signature,
            known: false,
            readNotBefore: 0,
            extendNotBefore: at.receivedAt + MISMATCH_RETRY_MS,
          },
        };
      }
      return { state: { ...next, stoppedFor: signature } };
  }
}

/** "4:05" for 245 000 ms. Whole seconds, rounded up, so 0:00 means over. */
export function clockText(msLeft: number): string {
  const secs = Math.max(0, Math.ceil(msLeft / 1000));
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
}

const minutesWord = (n: number) => `${n} ${n === 1 ? "minute" : "minutes"}`;

export type HoldTimerView =
  | { kind: "hidden" }
  | {
      kind: "countdown";
      /** The visible m:ss. */
      clock: string;
      /** The visible sentence beside it. */
      label: string;
      /** No more time is coming: at the cap, or the keep-alive stopped. */
      atCap: boolean;
      /** For the polite live region: changes once a minute, never each second. */
      announcement: string;
    }
  | { kind: "ended"; label: string; announcement: string };

export function timerView(s: KeepAliveState, now: number): HoldTimerView {
  if (s.ended) return { kind: "ended", label: LAPSE_MESSAGE, announcement: LAPSE_MESSAGE };
  const left = remainingMs(s, now);
  if (left == null || left > SHOW_AT_MS) return { kind: "hidden" };
  // Whole minutes, rounded up: "5 minutes" from 5:00 down to 4:01, then "4".
  const minutes = Math.ceil(Math.max(0, left) / 60_000);
  if (left <= 0) {
    return { kind: "countdown", clock: "0:00", label: "Checking your hold…", atCap: noMoreTime(s), announcement: "Checking your hold" };
  }
  if (noMoreTime(s)) {
    const text = `We can hold this for ${minutes} more ${minutes === 1 ? "minute" : "minutes"}`;
    return { kind: "countdown", clock: clockText(left), label: text, atCap: true, announcement: text };
  }
  return {
    kind: "countdown",
    clock: clockText(left),
    label: "left to finish your booking",
    atCap: false,
    announcement: `${minutesWord(minutes)} left to finish your booking`,
  };
}
